import {
	LitheCreationTypeEnum,
	LitheStepEnum,
	type TLitheQuestion,
	type TLitheQuestionApiResponse,
} from "../types/lithe.type";

const BACKEND_UG_CLEAN_DATA_STEP = "ug_clean_data";
const BACKEND_UG_FILTERS_STEP = "ug_filters";
const BACKEND_UG_TRANS_DEF_STEP = "ug_trans_def";
const BACKEND_UG_RF_SPECS_STEP = "ug_rf_specs";
const BACKEND_UG_APPROVAL_STEP = "ug_approval";

type TDataVizConstraintBlueprint = {
	lowerBound: number;
	upperBound: number;
};

const uiNonEditableQuestions: Record<
	string,
	Array<string>
> = {
	"00000201-0000-0000-0000-000000000000": [
		"00000053-0000-0000-0000-000000000000",
		"00000054-0000-0000-0000-000000000000",
		"00000055-0000-0000-0000-000000000000",
	],
};

const isUiEditable = (
	questionId: string,
	categoryId: string
) => {
	if (
		!Object.keys(uiNonEditableQuestions).includes(
			categoryId
		)
	)
		return true;
	else if (
		uiNonEditableQuestions[categoryId].includes(questionId)
	) {
		return false;
	} else return true;
};

const addConstraintsToDataVizQuestions: Record<
	string,
	TDataVizConstraintBlueprint
> = {
	"00000042-0000-0000-0000-000000000000": {
		lowerBound: 0,
		upperBound: 1740873599000,
	},

	"00000043-0000-0000-0000-000000000000": {
		lowerBound: 0,
		upperBound: 1740873599000,
	},

	"00000046-0000-0000-0000-000000000000": {
		lowerBound: 0,
		upperBound: 0.6,
	},

	"00000047-0000-0000-0000-000000000000": {
		lowerBound: 1000,
		upperBound: 100000,
	},

	"00000048-0000-0000-0000-000000000000": {
		lowerBound: 12,
		upperBound: 360,
	},
};

const normalizeLitheQuestionLabel = (question: string) => {
	return question.toLowerCase().trim();
};

export const isHardcodedLitheTypeQuestion = (
	question: Pick<TLitheQuestion, "question" | "questionId">
) => {
	return (
		question.questionId ===
			"00000056-0000-0000-0000-000000000000" ||
		normalizeLitheQuestionLabel(question.question) ===
			"lithe type"
	);
};

export const isLitheQuestionAnswered = (
	question: TLitheQuestion
) => {
	if (isHardcodedLitheTypeQuestion(question)) {
		return true;
	}

	const answer = question.answer;

	switch (question.answerType) {
		case "option_selection":
			return String(answer ?? "").trim().length > 0;
		case "float_list":
		case "int_list":
			if (Array.isArray(answer)) {
				return answer.length > 0;
			}

			if (typeof answer !== "string") {
				return false;
			}

			try {
				const parsed = JSON.parse(answer);
				return Array.isArray(parsed) && parsed.length > 0;
			} catch {
				return false;
			}
		default:
			if (Array.isArray(answer)) {
				return answer.length > 0;
			}

			if (typeof answer === "number") {
				return Number.isFinite(answer);
			}

			if (answer !== null && typeof answer === "object") {
				return (
					Object.keys(answer as Record<string, unknown>)
						.length > 0
				);
			}

			return String(answer ?? "").trim().length > 0;
	}
};

export const getIncompleteSpecsQuestions = (
	questions: Array<TLitheQuestion> | null | undefined
) => {
	if (!Array.isArray(questions)) {
		return [];
	}

	return questions.filter(
		(question) => !isLitheQuestionAnswered(question)
	);
};

const litheStepLabelMap: Record<LitheStepEnum, string> = {
	[LitheStepEnum.UGQUICKSTART]: "Paul - Autonomous",
	[LitheStepEnum.SPECS]: "Extract Data",
	[LitheStepEnum.UGCLEANDATA]: "Clean Data",
	[LitheStepEnum.UGFILTERS]: "Loan Filters",
	[LitheStepEnum.UGTRANSDEF]: "Transition Definition",
	[LitheStepEnum.UGFEATUREENG]: "Engineering Features",
	[LitheStepEnum.UGREPLINES]: "Risk Replines",
	[LitheStepEnum.RFSPECS]: "Model Params",
	[LitheStepEnum.APPROVAL]: "Approval",
	[LitheStepEnum.LITHEAPPROVED]: "Lithe Approved",
};

export const getLitheStepLabel = (
	stepId: LitheStepEnum,
	method?: LitheCreationTypeEnum
) => {
	if (
		stepId === LitheStepEnum.RFSPECS &&
		method === LitheCreationTypeEnum.UG
	) {
		return "Probability Analysis";
	}

	return litheStepLabelMap[stepId];
};

export const getLitheBackendStep = (
	step: string
): string => {
	const backendSteps: Record<string, string> = {
		[LitheStepEnum.UGCLEANDATA]: BACKEND_UG_CLEAN_DATA_STEP,
		[LitheStepEnum.UGFILTERS]: BACKEND_UG_FILTERS_STEP,
		[LitheStepEnum.UGTRANSDEF]: BACKEND_UG_TRANS_DEF_STEP,
		[LitheStepEnum.RFSPECS]: BACKEND_UG_RF_SPECS_STEP,
		[LitheStepEnum.APPROVAL]: BACKEND_UG_APPROVAL_STEP,
	};
	return backendSteps[step] ?? step;
};

export const normalizeLitheStep = (
	step?: string | null
): LitheStepEnum | null => {
	if (!step) {
		return null;
	}

	if (step === BACKEND_UG_CLEAN_DATA_STEP) {
		return LitheStepEnum.UGCLEANDATA;
	}
	if (step === BACKEND_UG_FILTERS_STEP) {
		return LitheStepEnum.UGFILTERS;
	}
	if (step === BACKEND_UG_TRANS_DEF_STEP) {
		return LitheStepEnum.UGTRANSDEF;
	}
	if (step === BACKEND_UG_RF_SPECS_STEP) {
		return LitheStepEnum.RFSPECS;
	}
	if (step === BACKEND_UG_APPROVAL_STEP) {
		return LitheStepEnum.APPROVAL;
	}

	if (
		Object.values(LitheStepEnum).includes(
			step as LitheStepEnum
		)
	) {
		return step as LitheStepEnum;
	}

	return null;
};

export const normalizeLitheSteps = (
	steps?: Array<string> | null
): Array<LitheStepEnum> => {
	if (!Array.isArray(steps)) {
		return [];
	}

	return Array.from(
		new Set(
			steps
				.map((step) => normalizeLitheStep(step))
				.filter(
					(step): step is LitheStepEnum => step !== null
				)
		)
	);
};

export const extractQuestionsLithe = (
	data: Array<TLitheQuestionApiResponse> | null
) => {
	if (!data) return [];
	const res: Array<TLitheQuestion> = data.map((value) => {
		let tempAnswer = value.answer;

		let masterOptions:
			| Array<unknown>
			| Record<string, unknown> = [];

		if (
			value.answer_type_info.answer_type === "time_millis"
		) {
			const normalizedDateValue =
				tempAnswer instanceof Date ||
				typeof tempAnswer === "string" ||
				typeof tempAnswer === "number"
					? tempAnswer
					: 0;
			tempAnswer = new Date(normalizedDateValue)
				.toISOString()
				.split("T")[0];
		} else if (
			value.answer_type_info.answer_type === "float_list"
		) {
			tempAnswer = JSON.stringify(value.answer);
		} else if (
			typeof value.answer === "object" &&
			value.category_id ===
				"00000201-0000-0000-0000-000000000000"
		) {
			tempAnswer = JSON.stringify(value.answer);
		}

		if (
			value.category_id ===
				"00000202-0000-0000-0000-000000000000" &&
			Object.keys(
				addConstraintsToDataVizQuestions
			).includes(value.question_id)
		) {
			const q =
				addConstraintsToDataVizQuestions[value.question_id];
			const ct = [
				{
					constraint_value: q.lowerBound,
					answer_constraint_type_id:
						"00000002-0000-0000-0000-000000000000",
					constraint_name: "min_value",
				},
				{
					constraint_value: q.upperBound,
					answer_constraint_type_id:
						"00000003-0000-0000-0000-000000000000",
					constraint_name: "max_value",
				},
			];
			value.answer_type_info.constraints = ct;
		}

		if (
			value.answer_type_info.answer_type ===
				"risk_factor_ranges" ||
			value.answer_type_info.answer_type ===
				"risk_factor_breakpoints"
		) {
			const rawOptions = value.answer_type_info.options;
			masterOptions =
				Array.isArray(rawOptions) ||
				typeof rawOptions === "object"
					? rawOptions
					: [];
		} else if (
			value.answer_type_info.answer_type ===
			"option_selection"
		) {
			const rawOptions = value.answer_type_info.options;
			const selectionOptions = Array.isArray(rawOptions)
				? rawOptions.map((option) => ({
						optionId: option.option_id,
						optionName: option.display_name,
					}))
				: rawOptions && typeof rawOptions === "object"
					? Object.entries(rawOptions).flatMap(
							([optionKey, rawOption]) => {
								if (
									typeof rawOption !== "object" ||
									rawOption === null
								) {
									return [];
								}

								const option = rawOption as {
									option_id?: unknown;
									display_name?: unknown;
								};
								const optionId = String(
									option.option_id ?? optionKey
								);
								const optionName = String(
									option.display_name ?? optionId
								);

								return [{ optionId, optionName }];
							}
						)
					: [];
			masterOptions = selectionOptions.filter(
				(option) =>
					typeof option.optionId === "string" &&
					option.optionId.length > 0 &&
					typeof option.optionName === "string"
			);
		}

		let isEditable = true;

		if (value.is_ui_editable !== undefined) {
			isEditable = value.is_ui_editable;
		} else {
			isEditable = isUiEditable(
				value.question_id,
				value.category_id
			);
		}

		return {
			question: value.question,
			questionId: value.question_id,
			answers: masterOptions,
			answerType: value.answer_type_info.answer_type,
			defaultAnswer: value.default_value ?? value.answer,
			validations: value.answer_type_info.constraints,
			isGenAIEnabled: value.is_genai_compatible,
			answer: tempAnswer,
			pythonValue: value.python_transformation_code ?? "",
			naturalTextValue: value.transform_description ?? "",
			categoryId: value.category_id,
			label: value.question,
			isProcessed: value.is_processed,
			isAnswered: value.is_answered ?? false,
			isValidAnswer: value.is_valid_answer ?? true,
			errorMessage: value.error_msg,
			isEditable,
			updatedTime:
				typeof value.updated_time === "number"
					? value.updated_time
					: undefined,
		};
	});
	return res;
};

export const litheStatusOptions = [
	{
		label: "Any Status",
		value: "any",
		backgroundColor: "#FFFFFF",
		fontColor: "#000000",
	},
	{
		label: "Lithe Initialized",
		value: "not_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Training Data Selection",
		value: "data_viz_started",
		backgroundColor: "#FFF6E5",
		fontColor: "#FFA800",
	},
	{
		label: "Risk Factor Filter Selection",
		value: "cashflow_viz_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Define Buckets",
		value: "probs_started",
		backgroundColor: "#EFF8FF",
		fontColor: "#2E90FA",
	},
	{
		label: "Training Data Ready",
		value: "data_viz_completed",
		backgroundColor: "#CCFBEF",
		fontColor: "#0E9384",
	},
	{
		label: "Risk Factors Filtered",
		value: "cashflow_viz_completed",
		backgroundColor: "#F4F3FF",
		fontColor: "#6938EF",
	},
	{
		label: "Probabilities Calculated",
		value: "probs_completed",
		backgroundColor: "#EDFCF2",
		fontColor: "#16B364",
	},
	{
		label: "Lithe Approved",
		value: "approval_completed",
		backgroundColor: "#EDFCF2",
		fontColor: "#16B364",
	},
];

export const getLitheStatus = (status: string) => {
	return (
		litheStatusOptions.find(
			(option) => option.value === status
		) ?? litheStatusOptions[0]
	);
};

const guidedLitheSteps = [
	LitheStepEnum.SPECS,
	LitheStepEnum.UGQUICKSTART,
	LitheStepEnum.UGCLEANDATA,
	LitheStepEnum.UGFILTERS,
	LitheStepEnum.UGTRANSDEF,
	LitheStepEnum.UGFEATUREENG,
	LitheStepEnum.UGREPLINES,
	LitheStepEnum.RFSPECS,
	LitheStepEnum.APPROVAL,
];

export const get_all_steps = (
	backendSteps: Array<LitheStepEnum> = []
): Array<LitheStepEnum> => {
	const normalizedBackendSteps = backendSteps.filter(
		(step) => step !== LitheStepEnum.LITHEAPPROVED
	);

	return normalizedBackendSteps.length
		? normalizedBackendSteps
		: [...guidedLitheSteps];
};

const normalizeLitheMessage = (message: string) => {
	const cleanedMessage = message
		.split("Traceback")[0]
		.replace(/^Error occurred:\s*/i, "")
		.replace(/\s+/g, " ")
		.trim();

	if (!cleanedMessage) {
		return "";
	}

	return cleanedMessage.length > 220
		? `${cleanedMessage.slice(0, 220).trim()}...`
		: cleanedMessage;
};

export const UG_STALE_RESULTS_MESSAGE =
	"Inputs changed since this run. Click Run Analysis again before continuing.";

export const getLitheErrorMessage = (
	error: unknown,
	fallback = "Something went wrong while loading this step."
) => {
	const candidateError = error as
		| string
		| {
				msg?: string;
				error_msg?: string;
				response?: {
					data?: {
						msg?: string;
						error_msg?: string;
					};
				};
		  }
		| null
		| undefined;

	const rawMessage =
		(typeof candidateError === "string"
			? candidateError
			: (candidateError?.msg ??
				candidateError?.error_msg ??
				candidateError?.response?.data?.msg ??
				candidateError?.response?.data?.error_msg)) ??
		fallback;

	const normalizedMessage =
		normalizeLitheMessage(rawMessage);
	return normalizedMessage || fallback;
};
